import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  Box, Typography, TextField, Grid, Button,
  FormControlLabel, Checkbox,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateField } from "@mui/x-date-pickers/DateField";
import SignatureCanvas from "react-signature-canvas";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/es";
import api from "../utils/api";
import { styleBox } from "../utils/Stylebox";
import Loading from "../Loading/Loading";
import swal from "sweetalert";
import "../../pages/stylefirma.css";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

const ORANGE = "#ED9519";


export default function EditarServicio({ rowdata, setEditingDataservicios, ListarServicios, setIsModalOpen, edit }) {
  const userToken = localStorage.getItem("user-token");
  const UrlServicios = api.urlAPI + "servicios";
  const padRef = useRef(null);

  const [loading, setLoading] = useState(false);

  // ─── Estado del formulario ─────────────────────────────────────────────────
  const [cliente,          setCliente]          = useState(rowdata.cliente || "");
  const [contacto,         setContacto]         = useState(rowdata.contacto || "");
  const [direccion,        setDireccion]         = useState(rowdata.direccion || "");
  const [localidad,        setLocalidad]         = useState(rowdata.localidad || "");
  const [provincia,        setProvincia]         = useState(rowdata.provincia || "");
  const [modelo,           setModelo]            = useState(rowdata.modelo || "");
  const [chasis,           setChasis]            = useState(rowdata.chasis || "");
  const [motor,            setMotor]             = useState(rowdata.motor || "");
  const [horometro,        setHorometro]         = useState(rowdata.horometro || "");
  const [hodometro,        setHodometro]         = useState(rowdata.hodometro || "");
  const [flagReparacion,   setFlagReparacion]    = useState(rowdata.tipoServicioReparacion === "1");
  const [flagServicio,     setFlagServicio]      = useState(rowdata.tipoServicioServicio === "1");
  const [flagGarantia,     setFlagGarantia]      = useState(rowdata.tipoServicioGarantia === "1");
  const [otros,            setOtros]             = useState(rowdata.tipoServicioOtros || "");
  const [falla,            setFalla]             = useState(rowdata.descripcionFalla || "");
  const [servicioRealizado,setServicioRealizado] = useState(rowdata.servicio || "");
  const [km,               setKm]               = useState(rowdata.kmRecorridos || "");
  const [tecnico,          setTecnico]           = useState(rowdata.tecnico || "");
  const [canvas,           setCanvas]            = useState(rowdata.firmaCliente || "");
  const [aclaracionFirma,  setAclaracionFirma]   = useState(rowdata.aclaracionCliente || "");
  const [dni,              setDni]               = useState(rowdata.dni || "");
  const [fecha,            setFecha]             = useState(dayjs(rowdata.fecha).tz(dayjs.tz.guess()));
  const [horaInicio,       setHoraInicio]        = useState(dayjs(rowdata.horaInicio));
  const [horaFin,          setHoraFin]           = useState(dayjs(rowdata.horaFin));
  const [lines,            setLines]             = useState(rowdata.repuestosPorServicios || []);

  const titulo = edit ? `Modificar — Servicio #${rowdata.id}` : `Visualizar — Servicio #${rowdata.id}`;

  const [fieldErrors, setFieldErrors] = useState({});

  // ─── Firma ────────────────────────────────────────────────────────────────
  const clearCanvas = useCallback(() => { padRef?.current?.clear(); setCanvas(""); }, []);
  const saveCanvas  = useCallback(() => setCanvas(padRef?.current?.toDataURL()), []);

  // ─── Repuestos ────────────────────────────────────────────────────────────
  const handleLineChange = useCallback((e, id, field) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: e.target.value } : l));
  }, []);
  const addLine = useCallback(() => {
    const maxId = lines.reduce((m, l) => Math.max(m, l.id), 0);
    setLines(p => [...p, { id: maxId + 1 }]);
  }, [lines]);
  const delLine = useCallback(() => setLines(p => p.length > 1 ? p.slice(0, -1) : p), []);
  const buildRepuestos = () => lines.map(({ id, ...r }) => ({ ...r, idServicio: rowdata.id }));

  // ─── Validación habilitación botón ─────────────────────────────────────────
  const isButtonEnabled = useMemo(() => (
    cliente && contacto && direccion && localidad && modelo && chasis && motor &&
    falla && servicioRealizado && tecnico && canvas && aclaracionFirma && km &&
    (flagReparacion || flagServicio || flagGarantia || otros !== "")
  ), [cliente, contacto, direccion, localidad, modelo, chasis, motor, falla,
      servicioRealizado, tecnico, canvas, aclaracionFirma, km, flagReparacion,
      flagServicio, flagGarantia, otros]);

  // ─── Guardar cambios ───────────────────────────────────────────────────────
  const editarRegistro = async () => {
    swal({ title: "¿Estás seguro?", text: `Se modificará el servicio #${rowdata.id}`, icon: "warning",
      buttons: ["Cancelar", "Guardar"], dangerMode: true }).then(async confirm => {
      if (!confirm) return;
      const body = {
        cliente, fecha, contacto, direccion, localidad, modelo, chasis, motor, hodometro, horometro,
        tipoServicioReparacion: flagReparacion, tipoServicioServicio: flagServicio,
        tipoServicioGarantia: flagGarantia, tipoServicioOtros: otros,
        descripcionFalla: falla, servicio: servicioRealizado, kmRecorridos: km,
        horaInicio, horaFin, tecnico, firmaTecnico: rowdata.firmaTecnico,
        firmaCliente: canvas, aclaracionCliente: aclaracionFirma,
        repuestosPorServicios: buildRepuestos(), provincia, dni,
      };
      const opts = { method: "PUT", headers: { "Content-type": "application/json; charset=UTF-8", "iron-token": userToken }, body: JSON.stringify(body) };
      try {
        setLoading(true);
        const res = await fetch(`${UrlServicios}/${rowdata.id}`, opts);
        setLoading(false);
        if (res.ok) {
          swal("¡Modificado!", `Servicio #${rowdata.id} actualizado correctamente.`, "success");
          setIsModalOpen(false); setEditingDataservicios(false); ListarServicios();
        } else {
          swal("Error", `No se pudo modificar el servicio #${rowdata.id}`, "error");
        }
      } catch { swal("Error", "Hubo un error al guardar.", "error"); }
    });
  };

  const disabled = !edit;
  const e = fieldErrors;

  const validateAll = useCallback(() => {
    const errs = {};
    if (!cliente)  errs.cliente = "Requerido";
    if (!contacto) errs.contacto = "Requerido";
    if (!direccion) errs.direccion = "Requerido";
    if (!localidad) errs.localidad = "Requerido";
    if (!provincia) errs.provincia = "Requerido";
    if (!modelo) errs.modelo = "Requerido";
    if (!chasis) errs.chasis = "Requerido";
    if (!motor)  errs.motor  = "Requerido";
    if (!horometro) errs.horometro = "Requerido";
    if (!flagReparacion && !flagServicio && !flagGarantia && !otros)
      errs.tipoServicio = "Seleccioná al menos un tipo";
    if (!falla) errs.falla = "Requerido";
    if (!servicioRealizado) errs.servicioRealizado = "Requerido";
    if (!km) errs.km = "Requerido";
    if (!canvas) errs.canvas = "Guardá la firma antes de continuar";
    if (!aclaracionFirma) errs.aclaracionFirma = "Requerido";
    if (!dni) errs.dni = "Requerido";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }, [cliente, contacto, direccion, localidad, provincia, modelo, chasis, motor,
      horometro, flagReparacion, flagServicio, flagGarantia, otros, falla, servicioRealizado,
      km, canvas, aclaracionFirma, dni]);

  const SectionHeader = ({ label }) => (
    <Typography variant="caption" sx={{
      display: "block", fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.07em", color: "#ED9519", mb: 1.5, mt: 0.5,
    }}>{label}</Typography>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "90vh", bgcolor: "background.paper" }}>

      {/* Header simple */}
      <Box sx={{ bgcolor: "#ED9519", flexShrink: 0, px: 2, py: 1.2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1a0e00", textAlign: "center" }}>
          {titulo}
        </Typography>
      </Box>

      {/* Contenido — todo a la vez */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }}>

        {/* 1. Cliente */}
        <SectionHeader label="Datos del cliente" />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={8}>
            <TextField fullWidth required size="small" label="Cliente" value={cliente}
              onChange={ev => setCliente(ev.target.value)} disabled error={Boolean(e.cliente)} helperText={e.cliente} />
          </Grid>
          <Grid item xs={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateField size="small" label="Fecha" value={fecha} onChange={setFecha} fullWidth disabled={disabled} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required size="small" label="Contacto" value={contacto}
              onChange={ev => setContacto(ev.target.value)} disabled={disabled} error={Boolean(e.contacto)} helperText={e.contacto} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required size="small" label="Dirección" value={direccion}
              onChange={ev => setDireccion(ev.target.value)} disabled={disabled} error={Boolean(e.direccion)} helperText={e.direccion} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required size="small" label="Localidad" value={localidad}
              onChange={ev => setLocalidad(ev.target.value)} disabled={disabled} error={Boolean(e.localidad)} helperText={e.localidad} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required size="small" label="Provincia" value={provincia}
              onChange={ev => setProvincia(ev.target.value)} disabled={disabled} error={Boolean(e.provincia)} helperText={e.provincia} />
          </Grid>
        </Grid>

        {/* 2. Máquina */}
        <SectionHeader label="Datos de la máquina" />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required size="small" label="Modelo" value={modelo}
              onChange={ev => setModelo(ev.target.value)} disabled={disabled} error={Boolean(e.modelo)} helperText={e.modelo} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth required size="small" label="N° Chasis" value={chasis}
              onChange={ev => setChasis(ev.target.value)} disabled={disabled} error={Boolean(e.chasis)} helperText={e.chasis} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField fullWidth required size="small" label="N° Motor" value={motor}
              onChange={ev => setMotor(ev.target.value)} disabled={disabled} error={Boolean(e.motor)} helperText={e.motor} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth required size="small" label="Horómetro" value={horometro}
              onChange={ev => setHorometro(ev.target.value)} disabled={disabled} error={Boolean(e.horometro)} helperText={e.horometro} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Hodómetro" value={hodometro}
              onChange={ev => setHodometro(ev.target.value)} disabled={disabled} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>Tipo de servicio</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              {[
                { val: flagReparacion, set: setFlagReparacion, label: "Reparación", dis: otros !== "" || flagServicio || flagGarantia },
                { val: flagServicio,   set: setFlagServicio,   label: "Servicio",   dis: otros !== "" || flagReparacion || flagGarantia },
                { val: flagGarantia,   set: setFlagGarantia,   label: "Garantía",   dis: otros !== "" || flagServicio || flagReparacion },
              ].map(({ val, set, label, dis }) => (
                <FormControlLabel key={label} label={<Typography variant="body2">{label}</Typography>}
                  control={<Checkbox size="small" checked={val} disabled={disabled || dis}
                    onChange={ev => set(ev.target.checked)} />} />
              ))}
            </Box>
            {e.tipoServicio && <Typography variant="caption" color="error">{e.tipoServicio}</Typography>}
            <TextField fullWidth size="small" label="Otros" value={otros} sx={{ mt: 1 }}
              onChange={ev => setOtros(ev.target.value)} disabled={disabled || flagReparacion || flagServicio || flagGarantia} />
          </Grid>
        </Grid>

        {/* 3. Descripción */}
        <SectionHeader label="Descripción" />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField fullWidth required size="small" label="Descripción de la falla" multiline minRows={2} maxRows={4}
              value={falla} onChange={ev => setFalla(ev.target.value)} disabled={disabled}
              error={Boolean(e.falla)} helperText={e.falla} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth required size="small" label="Servicio realizado" multiline minRows={2} maxRows={4}
              value={servicioRealizado} onChange={ev => setServicioRealizado(ev.target.value)} disabled={disabled}
              error={Boolean(e.servicioRealizado)} helperText={e.servicioRealizado} />
          </Grid>
        </Grid>

        {/* 4. Repuestos */}
        <SectionHeader label="Repuestos" />
        <Box sx={{ mb: 2 }}>
          {lines.map(line => (
            <Grid container spacing={1} key={line.id} sx={{ mb: 1 }}>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Cant." type="number" disabled={disabled}
                  value={line.cantidad || ""} onChange={ev => handleLineChange(ev, line.id, "cantidad")} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Código" disabled={disabled}
                  value={line.codigo || ""} onChange={ev => handleLineChange(ev, line.id, "codigo")} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Descripción" disabled={disabled}
                  value={line.descripcion || ""} onChange={ev => handleLineChange(ev, line.id, "descripcion")} />
              </Grid>
            </Grid>
          ))}
          {edit && (
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              <Button size="small" variant="outlined" onClick={addLine} sx={{ flex: 1 }}>+ Agregar</Button>
              <Button size="small" variant="outlined" color="warning" onClick={delLine} sx={{ flex: 1 }} disabled={lines.length <= 1}>− Eliminar</Button>
            </Box>
          )}
        </Box>

        {/* 5. Técnico */}
        <SectionHeader label="Datos del técnico" />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Técnico interviniente" value={tecnico} disabled />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required size="small" label="Km. recorridos" type="number" value={km}
              onChange={ev => setKm(ev.target.value)} disabled={disabled}
              error={Boolean(e.km)} helperText={e.km} />
          </Grid>
          <Grid item xs={6} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker label="Hora inicio" value={horaInicio} onChange={setHoraInicio} disabled={disabled} ampm={false}
                slotProps={{ textField: { size: "small", fullWidth: true } }} />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker label="Hora fin" value={horaFin} onChange={setHoraFin} disabled={disabled} ampm={false}
                slotProps={{ textField: { size: "small", fullWidth: true } }} />
            </LocalizationProvider>
          </Grid>
        </Grid>

        {/* 6. Firma */}
        <SectionHeader label="Firma del cliente" />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            {edit ? (
              <>
                <Box sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2, overflow: "hidden", bgcolor: "#fff", width: 280, height: 160 }}>
                  <SignatureCanvas ref={padRef} penColor="#111318"
                    canvasProps={{ width: 280, height: 160, style: { display: "block" } }} />
                </Box>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Button size="small" variant="outlined" onClick={clearCanvas}>Limpiar</Button>
                  <Button size="small" variant="contained" onClick={saveCanvas}>Guardar firma</Button>
                </Box>
                {e.canvas && <Typography variant="caption" color="error">{e.canvas}</Typography>}
              </>
            ) : (
              <Box sx={{ border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
                <img src={canvas} alt="Firma del cliente" style={{ display: "block", maxWidth: 280 }} />
              </Box>
            )}
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <TextField fullWidth required size="small" label="DNI cliente" value={dni}
                onChange={ev => setDni(ev.target.value)} disabled={disabled}
                error={Boolean(e.dni)} helperText={e.dni} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth required size="small" label="Aclaración firma" value={aclaracionFirma}
                onChange={ev => setAclaracionFirma(ev.target.value)} disabled={disabled}
                error={Boolean(e.aclaracionFirma)} helperText={e.aclaracionFirma} />
            </Grid>
          </Grid>
        </Box>

      </Box>

      {/* Footer */}
      <Box sx={{
        flexShrink: 0, px: 2, py: 1.5,
        borderTop: "1px solid", borderColor: "divider",
        display: "flex", justifyContent: "flex-end", gap: 1,
      }}>
        <Button variant="outlined" size="small" onClick={() => setIsModalOpen(false)}>
          Cancelar
        </Button>
        {edit && (
          <Button variant="contained" size="small" color="success" startIcon={<SendIcon sx={{ fontSize: 16 }} />}
            disabled={loading}
            onClick={() => { if (validateAll()) editarRegistro(); }}>
            Guardar cambios
          </Button>
        )}
      </Box>

      {loading && <Loading />}
    </Box>
  );
}
